import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Coffee,
  Menu,
  ShoppingCart,
  BarChart3,
  ChefHat,
  QrCode,
  Clock,
  TrendingUp,
  X,
  Plus,
  Minus,
  CheckCircle2,
  AlertCircle,
  Zap,
  Heart,
  Award,
  MapPin,
  Phone,
  Instagram,
  Trash2,
  CheckSquare,
} from "lucide-react";
import DemoBackBar from "../components/DemoBackBar";
import { paloCafe } from "../data/demos";

const PAGES = {
  HOME: "home",
  MENU: "menu",
  ADMIN: "admin",
  ANALYTICS: "analytics",
};

export default function PaloCafeDemo() {
  const [currentPage, setCurrentPage] = useState(PAGES.HOME);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [showPromo, setShowPromo] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(paloCafe.menu[0].category);
  const [orders, setOrders] = useState(paloCafe.sampleOrders);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const addToCart = (item) => {
    const existing = cart.find((c) => c.id === item.id);
    if (existing) {
      setCart(cart.map((c) => (c.id === item.id ? { ...c, qty: c.qty + 1 } : c)));
    } else {
      setCart([...cart, { ...item, qty: 1 }]);
    }
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) {
      setCart(cart.filter((c) => c.id !== id));
    } else {
      setCart(cart.map((c) => (c.id === id ? { ...c, qty } : c)));
    }
  };

  const placeOrder = () => {
    if (!customerName || !tableNumber || cart.length === 0) {
      alert("Please fill all fields and add items");
      return;
    }
    const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
    const points = Math.floor(total / 10);
    const newOrder = {
      id: `ORD-${String(Math.floor(Math.random() * 10000)).padStart(3, "0")}`,
      name: customerName,
      items: cart,
      notes,
      table: tableNumber,
      total,
      status: "Pending",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    setOrderPlaced(newOrder);
    setLoyaltyPoints(loyaltyPoints + points);
    setCart([]);
    setCustomerName("");
    setTableNumber("");
    setNotes("");
  };

  const updateOrderStatus = (orderId, newStatus) => {
    setOrders(orders.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
  };

  const cancelOrder = (orderId) => {
    setOrders(orders.filter((o) => o.id !== orderId));
  };

  const currentPageContent = () => {
    switch (currentPage) {
      case PAGES.HOME:
        return <HomePage setCurrentPage={setCurrentPage} loyaltyPoints={loyaltyPoints} />;
      case PAGES.MENU:
        return (
          <MenuPage
            cart={cart}
            addToCart={addToCart}
            updateQty={updateQty}
            customerName={customerName}
            setCustomerName={setCustomerName}
            tableNumber={tableNumber}
            setTableNumber={setTableNumber}
            notes={notes}
            setNotes={setNotes}
            placeOrder={placeOrder}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            orderPlaced={orderPlaced}
            setOrderPlaced={setOrderPlaced}
          />
        );
      case PAGES.ADMIN:
        return (
          <AdminPage
            orders={orders}
            updateOrderStatus={updateOrderStatus}
            cancelOrder={cancelOrder}
            tasks={paloCafe.baristaTasks}
            analytics={paloCafe.analytics}
          />
        );
      case PAGES.ANALYTICS:
        return <AnalyticsPage analytics={paloCafe.analytics} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#160f0c] text-[#f7f1ea]">
      <DemoBackBar accentColor="#B8784E" demoName="Palo Café · Full Café System" />

      {/* Navigation */}
      <nav className="sticky top-[40px] z-40 border-b border-white/8 bg-[#1f1511]/85 backdrop-blur-md">
        <div className="mx-auto hidden max-w-7xl gap-2 overflow-x-auto px-4 py-4 scrollbar-none md:flex sm:px-8">
          <NavButton
            isActive={currentPage === PAGES.HOME}
            onClick={() => setCurrentPage(PAGES.HOME)}
            icon={<Coffee size={16} />}
            label="Home"
          />
          <NavButton
            isActive={currentPage === PAGES.MENU}
            onClick={() => setCurrentPage(PAGES.MENU)}
            icon={<ShoppingCart size={16} />}
            label="Menu & Order"
            badge={cart.length > 0 ? cart.length : null}
          />
          <NavButton
            isActive={currentPage === PAGES.ADMIN}
            onClick={() => setCurrentPage(PAGES.ADMIN)}
            icon={<ChefHat size={16} />}
            label="Admin"
          />
          <NavButton
            isActive={currentPage === PAGES.ANALYTICS}
            onClick={() => setCurrentPage(PAGES.ANALYTICS)}
            icon={<BarChart3 size={16} />}
            label="Analytics"
          />
          <div className="ml-auto flex items-center gap-2 rounded-lg border border-[#5b4032] bg-[#2a1d17]/80 px-4 py-2">
            <Award size={16} style={{ color: "#e2b78d" }} />
            <span className="text-sm font-semibold text-[#f3dcc8]">{loyaltyPoints} points</span>
          </div>
        </div>

        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:hidden">
          <button
            onClick={() => setMobileNavOpen((open) => !open)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[#5b4032] bg-[#2a1d17]/80 text-[#f6eadf] transition-colors hover:border-[#8f5e3d]"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
          >
            <Menu size={20} />
          </button>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2 rounded-lg border border-[#5b4032] bg-[#2a1d17]/80 px-3 py-2">
            <Award size={16} style={{ color: "#e2b78d" }} />
            <span className="truncate text-sm font-semibold text-[#f3dcc8]">{loyaltyPoints} points</span>
          </div>
        </div>

        <AnimatePresence>
          {mobileNavOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="border-t border-white/8 bg-[#1f1511]/98 px-4 py-4 md:hidden"
            >
              <div className="mx-auto max-w-7xl space-y-2">
                <button
                  onClick={() => {
                    setCurrentPage(PAGES.HOME);
                    setMobileNavOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold ${
                    currentPage === PAGES.HOME
                      ? "border-[#8f5e3d] bg-[#302018] text-[#f0d5bb]"
                      : "border-white/10 bg-[#231813]/80 text-[#dbc3b2]"
                  }`}
                >
                  <Coffee size={18} />
                  Home
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(PAGES.MENU);
                    setMobileNavOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold ${
                    currentPage === PAGES.MENU
                      ? "border-[#8f5e3d] bg-[#302018] text-[#f0d5bb]"
                      : "border-white/10 bg-[#231813]/80 text-[#dbc3b2]"
                  }`}
                >
                  <ShoppingCart size={18} />
                  Menu & Order
                  {cart.length > 0 && (
                    <span className="ml-auto inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#b97b4b] px-1 text-xs font-bold text-[#1d120d]">
                      {cart.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(PAGES.ADMIN);
                    setMobileNavOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold ${
                    currentPage === PAGES.ADMIN
                      ? "border-[#8f5e3d] bg-[#302018] text-[#f0d5bb]"
                      : "border-white/10 bg-[#231813]/80 text-[#dbc3b2]"
                  }`}
                >
                  <ChefHat size={18} />
                  Admin
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(PAGES.ANALYTICS);
                    setMobileNavOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold ${
                    currentPage === PAGES.ANALYTICS
                      ? "border-[#8f5e3d] bg-[#302018] text-[#f0d5bb]"
                      : "border-white/10 bg-[#231813]/80 text-[#dbc3b2]"
                  }`}
                >
                  <BarChart3 size={18} />
                  Analytics
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Promo Banner */}
      <AnimatePresence>
        {showPromo && currentPage === PAGES.MENU && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mx-4 mt-4 flex items-start justify-between gap-4 rounded-lg border border-[#6d4a35] bg-gradient-to-r from-[#241812]/90 via-[#342116]/85 to-[#1c140f]/90 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.28)] sm:items-center sm:p-6"
          >
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#efbf93]">
                {paloCafe.promotion.badge}
              </div>
              <p className="mt-2 text-lg font-bold text-[#f6e8dc]">{paloCafe.promotion.description}</p>
              <p className="mt-1 text-sm text-[#d9b08a]">{paloCafe.promotion.discount}</p>
            </div>
            <button
              onClick={() => setShowPromo(false)}
              className="flex-shrink-0 text-[#d7a37a] transition-colors hover:text-[#f2caa9]"
            >
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page Content */}
      <div className="mx-auto max-w-7xl">{currentPageContent()}</div>
    </div>
  );
}

function NavButton({ isActive, onClick, icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 inline-flex items-center gap-2 rounded-lg border px-5 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
        isActive
          ? "border-[#8f5e3d] bg-[#302018] text-[#f0d5bb]"
          : "border-white/10 bg-transparent text-[#dbc3b2] hover:border-white/20 hover:text-[#f8eee5]"
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      {badge && (
        <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#b97b4b] text-xs font-bold text-[#1d120d]">
          {badge}
        </span>
      )}
    </button>
  );
}

function HomePage({ setCurrentPage, loyaltyPoints }) {
  return (
    <div className="space-y-0">
      {/* Hero */}
      <section className="relative overflow-hidden px-4 py-16 sm:px-8 sm:py-20 min-h-[60vh] md:min-h-[88vh]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${paloCafe.hero})` }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(105deg,rgba(20,12,9,0.95)_0%,rgba(20,12,9,0.86)_48%,rgba(20,12,9,0.4)_100%)]" />
        <div className="relative mx-auto grid max-w-5xl items-center gap-8 md:gap-12">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="text-xs font-bold uppercase tracking-[0.4em] text-[#efbf93]"
            >
              Full Café System
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="mt-6 text-4xl font-black leading-tight tracking-tight sm:text-6xl lg:text-7xl"
            >
              A warmer café experience,
              <span className="text-[#e9c29d]"> designed end to end</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12 }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-[#f4e5d8] sm:mt-8 sm:text-lg"
            >
              Order, loyalty, barista workflow, and analytics in one polished café system. Built for coffee shops that want a better guest experience and a smoother team flow.
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.16 }}
              onClick={() => setCurrentPage("menu")}
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#b8784e] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#a96739] sm:mt-10 sm:px-8 sm:py-4 sm:text-sm"
            >
              <ShoppingCart size={18} /> Explore the café
            </motion.button>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="px-4 py-16 sm:px-8 sm:py-20 bg-[#1b130f]">
        <div className="mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden rounded-[28px] border-2 border-[#8f5e3d] bg-gradient-to-b from-[#2f2018]/90 to-[#1c140f]/90 shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-0">
              <div className="overflow-hidden">
                <img
                  src={encodeURI(`${process.env.PUBLIC_URL}/our story 3.webp`)}
                  alt="Palo Café story"
                  className="h-full min-h-[300px] md:min-h-[400px] w-full object-cover"
                />
              </div>
              <div className="p-8 sm:p-10 md:p-12 flex flex-col justify-center">
                <div className="mb-6 flex items-center gap-3">
                  <div className="h-1 w-12 bg-gradient-to-r from-[#efbf93] to-[#b8784e]"></div>
                  <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#efbf93]">Our Heritage</p>
                </div>
                <h2 className="mb-6 text-3xl sm:text-4xl font-bold text-[#f6eadf] leading-tight">Our Story</h2>
                <p className="mb-5 leading-relaxed text-[#e9d7c8] text-sm sm:text-base">{paloCafe.story}</p>
                <p className="text-[#cfae94] text-sm leading-relaxed italic">We believe in quality over quantity, sustainability over trends, and creating spaces where people genuinely connect.</p>
                <div className="mt-8 pt-6 border-t border-[#5b4032]">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#8f7d6b] font-semibold">✨ Premium Sourced • Artisan Crafted ✨</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Loyalty Card */}
      <section className="px-4 py-20 sm:px-8">
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-[28px] border border-[#6d4a35] bg-gradient-to-r from-[#241812] via-[#302018] to-[#1c140f] p-12 text-center shadow-[0_24px_80px_rgba(0,0,0,0.25)]"
          >
            <Award size={48} className="mx-auto mb-4" style={{ color: "#e2b78d" }} />
            <h2 className="mb-4 text-3xl font-bold text-[#f7eee4]">Palo Loyalty Card</h2>
            <p className="mb-8 text-[#e9d7c8]">Collect points with every order and enjoy exclusive rewards</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 text-center">
              {paloCafe.loyaltyInfo.benefits.map((benefit, i) => (
                <div key={i} className="rounded-xl border border-[#5b4032] bg-[#2a1d17]/85 p-4">
                  <p className="text-sm font-semibold text-[#f0d2b7]">{benefit}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 font-bold text-[#f3e1d3]">Your current points: <span className="text-2xl text-[#efbf93]">{loyaltyPoints}</span></p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-4 py-20 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#efbf93]">Loved by our community</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-[#f6eadf]">What Our Customers Say</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {paloCafe.testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-[20px] border border-[#5b4032] bg-[#241812] p-6 sm:p-8 flex flex-col"
              >
                <div className="mb-4 flex gap-1">
                  {Array.from({ length: testimonial.stars }).map((_, j) => (
                    <span key={j} className="text-[#efbf93]">★</span>
                  ))}
                </div>
                <p className="mb-6 flex-1 text-[#e9d7c8]">{testimonial.quote}</p>
                <div className="border-t border-white/8 pt-4">
                  <p className="font-semibold text-[#f6eadf]">{testimonial.name}</p>
                  <p className="text-xs text-[#cfae94]">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coffee Education */}
      <section className="px-4 py-20 sm:px-8 bg-[#1b130f]">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#efbf93]">Coffee Crafted</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-[#f6eadf]">The Roast Profiles</h2>
            <p className="mt-4 max-w-2xl mx-auto text-[#e9d7c8]">Each roast brings out unique flavors. Learn which profile matches your taste.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { name: "Light Roast", desc: "Bright, fruity, more caffeine. Best for: Pour overs, single origins", color: "#c9a87c" },
              { name: "Medium Roast", desc: "Balanced, smooth, versatile. Best for: Espresso, cappuccinos, daily drinks", color: "#9d7a56" },
              { name: "Dark Roast", desc: "Bold, rich, low acidity. Best for: Strong coffee lovers, cold brew", color: "#6b5544" },
            ].map((roast, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-[20px] border border-[#5b4032] bg-[#241812] p-6 sm:p-8 hover:border-[#8f5e3d] transition-colors"
              >
                <div className="mb-4 h-12 w-12 rounded-lg" style={{ background: roast.color }} />
                <h3 className="text-xl font-bold text-[#f6eadf] mb-3">{roast.name}</h3>
                <p className="text-sm text-[#e9d7c8]">{roast.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties */}
      <section className="px-4 py-20 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-16 text-center text-3xl sm:text-4xl font-bold text-[#f6eadf]">Our Signature Drinks</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {paloCafe.specialties.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="overflow-hidden rounded-[24px] border border-[#5b4032] bg-[#241812] transition-colors hover:border-[#b97b4b]"
              >
                <img
                  src={item.img}
                  alt={item.name}
                  className="aspect-square w-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="bg-[#241812]/95 p-6">
                  <h3 className="text-lg font-bold text-[#f6eadf]">{item.name}</h3>
                  <p className="mt-2 text-sm text-[#cfb49f]">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-4 py-20 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#efbf93]">More photos</p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#f6eadf]">A café people want to spend time in</h2>
            </div>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {(paloCafe.gallery || []).map((photo, index) => (
              <motion.img
                key={photo}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                src={photo}
                alt={`Palo Café gallery ${index + 1}`}
                className="w-full rounded-[20px] sm:rounded-[24px] object-cover shadow-[0_24px_80px_rgba(0,0,0,0.24)] h-56 sm:h-64 md:h-64"
              />
            ))}
          </div>
        </div>
      </section>

      {/* See Our Menu */}
      <section className="px-4 sm:px-8 py-20">
        <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.img
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            src={encodeURI(`${process.env.PUBLIC_URL}/scaning qr code.jpg`)}
            alt="Menu"
            className="w-full rounded-[20px] sm:rounded-[28px] border border-white/10 object-cover shadow-[0_24px_80px_rgba(0,0,0,0.25)] h-64 sm:h-80"
          />
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-6 text-3xl sm:text-4xl font-bold text-[#f6eadf]">Explore Our Full Menu</h2>
            <p className="mb-8 text-sm sm:text-base leading-relaxed text-[#e9d7c8]">From hot espresso classics to refreshing iced drinks, mojitos, smoothies, and freshly baked pastries. Every item is crafted with care using premium ingredients.</p>
            <button
              onClick={() => setCurrentPage("menu")}
              className="inline-flex items-center gap-3 rounded-lg bg-[#b8784e] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#a96739]"
            >
              <ShoppingCart size={20} /> See Menu & Order
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-20 border-t border-white/8 bg-[#130c09]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-12">
            <div>
              <h3 className="mb-4 text-xl font-bold text-[#f7eee4]">Palo Café</h3>
              <p className="text-sm text-[#cfae94]">Premium coffee moments, crafted for you.</p>
            </div>
            <div>
              <h4 className="mb-4 font-bold text-[#f1d2b8]">Hours</h4>
              {paloCafe.hours.map((h, i) => (
                <p key={i} className="text-sm text-[#cfae94]">{h.day}: {h.time}</p>
              ))}
            </div>
            <div>
              <h4 className="mb-4 font-bold text-[#f1d2b8]">Contact</h4>
              <a href={`tel:${paloCafe.phone}`} className="mb-3 flex items-center gap-2 text-[#cfae94] hover:text-[#f7e1cd]">
                <Phone size={16} /> {paloCafe.phone}
              </a>
              <div className="flex items-center gap-2 text-[#cfae94]">
                <MapPin size={16} /> {paloCafe.address}
              </div>
            </div>
            <div>
              <h4 className="mb-4 font-bold text-[#f1d2b8]">Follow Us</h4>
              <a href="#" className="flex items-center gap-2 text-[#cfae94] hover:text-[#f7e1cd]">
                <Instagram size={16} /> @paloCafe
              </a>
            </div>
          </div>
          <div className="border-t border-white/8 pt-8 text-center text-sm text-[#b79683]">
            <p>© 2026 Palo Café. Premium coffee experience. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MenuPage({
  cart,
  addToCart,
  updateQty,
  customerName,
  setCustomerName,
  tableNumber,
  setTableNumber,
  notes,
  setNotes,
  placeOrder,
  selectedCategory,
  setSelectedCategory,
  orderPlaced,
  setOrderPlaced,
}) {
  if (orderPlaced) {
    return <OrderConfirmation order={orderPlaced} onClose={() => setOrderPlaced(null)} />;
  }

  const currentItems = paloCafe.menu.find((m) => m.category === selectedCategory)?.items || [];
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);

  return (
    <div className="grid grid-cols-1 gap-6 px-4 py-12 sm:px-8 lg:grid-cols-3">
      {/* Menu */}
      <div className="lg:col-span-2 space-y-6">
        {/* Today's Special */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-[20px] border-2 border-[#efbf93] bg-gradient-to-r from-[#2f2018]/90 to-[#241812]/90 p-4 sm:p-6"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#efbf93] mb-1">✨ Today's Special ✨</p>
              <p className="font-semibold text-[#f6eadf]">Cappuccino + Croissant Combo</p>
              <p className="text-sm text-[#e9d7c8] mt-1">Save 3 NIS • Morning Special (7 AM - 12 PM)</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="line-through text-[#8f7d6b] text-sm">19 NIS</p>
              <p className="text-2xl font-bold text-[#efbf93]">16 NIS</p>
            </div>
          </div>
        </motion.div>

        {/* Category Tabs */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
          {paloCafe.menu.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={`rounded-lg border px-4 py-2.5 text-center text-sm font-semibold transition-all sm:flex-shrink-0 sm:px-5 ${
                selectedCategory === cat.category
                  ? "border-[#8f5e3d] bg-[#2f2018] text-[#f6eadf]"
                  : "border-white/10 bg-[#231813]/80 text-[#dbc3b2] hover:border-white/20"
              }`}
            >
              {cat.category}
            </button>
          ))}
        </div>

        {/* Barista's Recommendation */}
        {currentItems.some((item) => item.popular) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[20px] border border-[#8f5e3d] bg-gradient-to-r from-[#2f2018]/80 to-[#241812]/80 p-4 sm:p-6 mb-6"
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="rounded-full bg-[#b8784e] p-2 flex-shrink-0">
                <Coffee size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#efbf93] mb-1">Barista's Recommendation</p>
                <p className="text-sm text-[#e9d7c8]">
                  Try our most beloved drinks selected by our expert baristas for their perfect balance of flavor and craftsmanship.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Menu Items */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
          {currentItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileHover={{ y: -8 }}
              className="group overflow-hidden rounded-[24px] border border-[#5b4032] bg-[#241812] transition-all hover:border-[#b97b4b] hover:shadow-[0_30px_60px_rgba(184,120,78,0.2)] cursor-pointer"
            >
              <div className="relative mb-0 overflow-hidden h-56 bg-[#1a0f0a]">
                <img
                  src={item.img}
                  alt={item.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-125"
                />
                <div className="absolute top-3 right-3 flex gap-2 flex-wrap justify-end">
                  {item.bestseller && (
                    <motion.span whileHover={{ scale: 1.1 }} className="rounded-full bg-[#efbf93] px-3 py-1.5 text-xs font-bold text-[#1d120d] shadow-lg">
                      ⭐ Bestseller
                    </motion.span>
                  )}
                  {item.popular && !item.bestseller && (
                    <motion.span whileHover={{ scale: 1.1 }} className="rounded-full bg-[#b97b4b]/90 px-3 py-1.5 text-xs font-bold text-white shadow-lg">
                      ♡ Popular
                    </motion.span>
                  )}
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-[#f6eadf]">{item.name}</h3>
                <p className="mt-2 text-xs sm:text-sm text-[#cfae94] leading-relaxed">{item.desc}</p>
                {item.origin && (
                  <div className="mt-4 pt-4 border-t border-[#5b4032] space-y-2">
                    <p className="text-xs text-[#8f7d6b] font-medium flex items-center gap-2">
                      <span className="text-[#efbf93]">☕</span> {item.origin}
                    </p>
                    <p className="text-xs text-[#8f7d6b] font-medium flex items-center gap-2">
                      <span className="text-[#efbf93]">🔥</span> {item.roast} Roast
                    </p>
                  </div>
                )}
                <div className="mt-5 flex items-center justify-between gap-2">
                  <span className="font-bold text-[#efbf93]">{item.price} NIS</span>
                  <button
                    onClick={() => addToCart(item)}
                    className="inline-flex items-center gap-1 rounded-lg bg-[#b8784e] px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-[#a96739] hover:shadow-md"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Cart & Checkout */}
      <div className="space-y-4">
        <div className="space-y-4 lg:sticky lg:top-[120px]">
          {/* Cart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 rounded-[24px] border-2 border-[#8f5e3d] bg-gradient-to-b from-[#2f2018]/95 to-[#1c140f]/95 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.4)]"
          >
            <div className="flex items-center justify-between pb-4 border-b border-[#5b4032]">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-[#b8784e] p-2.5">
                  <ShoppingCart size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#f6eadf]">Your Order</h3>
              </div>
              {cart.length > 0 && (
                <motion.span
                  initial={{ scale: 1.3 }}
                  animate={{ scale: 1 }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-[#efbf93] to-[#b8784e] text-xs font-bold text-[#1d120d] shadow-lg"
                >
                  {cart.length}
                </motion.span>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="py-16 text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#5b4032]/50 mb-4">
                  <Coffee size={32} className="text-[#8f7d6b]" />
                </div>
                <p className="text-sm text-[#cfae94] font-medium">Your cart is waiting for its first perfect drink</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {cart.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between rounded-xl bg-[#32231c] p-3 hover:bg-[#3a2820] transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-semibold text-[#f6eadf]">{item.name}</p>
                        <p className="text-xs text-[#cfae94]">{item.price} NIS × {item.qty}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-2 bg-[#241812] rounded-lg px-2 py-1">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="rounded p-0.5 text-[#cfae94] hover:text-[#efbf93] transition-colors"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-5 text-center text-xs font-bold text-[#f6eadf]">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="rounded p-0.5 text-[#cfae94] hover:text-[#efbf93] transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Total */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-t border-white/8 pt-4 space-y-2"
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#cfae94]">Subtotal</span>
                    <span className="text-[#f6eadf]">{cartTotal} NIS</span>
                  </div>
                  <div className="flex items-center justify-between font-bold text-lg">
                    <span className="text-[#f6eadf]">Total</span>
                    <span className="text-[#efbf93]">{cartTotal} NIS</span>
                  </div>
                </motion.div>
              </>
            )}
          </motion.div>

          {/* Checkout Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-5 rounded-[24px] border-2 border-[#8f5e3d] bg-gradient-to-b from-[#2f2018]/95 to-[#1c140f]/95 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.4)]"
          >
            <div className="flex items-center gap-3 pb-4 border-b border-[#5b4032]">
              <div className="rounded-full bg-[#b8784e] p-2.5">
                <CheckCircle2 size={20} style={{ color: "#f6eadf" }} />
              </div>
              <h3 className="font-bold text-lg text-[#f6eadf]">Order Details</h3>
            </div>
            <input
              type="text"
              placeholder="Your name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full rounded-xl border border-[#5b4032] bg-[#32231c]/80 px-4 py-3 text-sm text-[#f7f1ea] placeholder:text-[#8f7d6b] focus:border-[#b8784e] focus:outline-none focus:ring-2 focus:ring-[#b8784e]/30 transition-all shadow-inner"
            />
            <select
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              className="w-full rounded-xl border border-[#5b4032] bg-[#32231c]/80 px-4 py-3 text-sm text-[#f7f1ea] focus:border-[#b8784e] focus:outline-none focus:ring-2 focus:ring-[#b8784e]/30 transition-all shadow-inner"
            >
              <option value="">Select location</option>
              {Array.from({ length: 12 }).map((_, i) => (
                <option key={i + 1} value={`Table ${i + 1}`}>
                  Table {i + 1}
                </option>
              ))}
              <option value="Pickup">Pickup at Counter</option>
            </select>
            <textarea
              placeholder="Special requests (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-20 w-full resize-none rounded-xl border border-[#5b4032] bg-[#32231c]/80 px-4 py-3 text-sm text-[#f7f1ea] placeholder:text-[#8f7d6b] focus:border-[#b8784e] focus:outline-none focus:ring-2 focus:ring-[#b8784e]/30 transition-all shadow-inner"
            />
            <motion.button
              onClick={placeOrder}
              disabled={cart.length === 0}
              whileHover={cart.length > 0 ? { scale: 1.05 } : {}}
              whileTap={cart.length > 0 ? { scale: 0.98 } : {}}
              className="w-full rounded-xl bg-gradient-to-r from-[#b8784e] via-[#a96739] to-[#9a5b30] py-4 text-sm font-bold uppercase tracking-widest text-white transition-all hover:shadow-[0_20px_50px_rgba(184,120,78,0.3)] disabled:cursor-not-allowed disabled:from-[#5a4336] disabled:via-[#4a3a2f] disabled:to-[#3a2a1f] disabled:opacity-50 border border-[#d4956a]/30"
            >
              {cart.length === 0 ? "Add items to order" : `Place Order • ${cartTotal} NIS`}
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function OrderConfirmation({ order, onClose }) {
  const [status, setStatus] = useState(order.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md rounded-[24px] border border-[#6d4a35] bg-[#221813] p-8 space-y-6"
      >
        <div className="text-center">
          <CheckCircle2 size={48} className="mx-auto text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-[#f6eadf]">Order Placed!</h2>
          <p className="mt-2 text-sm text-[#cfb49f]">Your order has been confirmed</p>
        </div>

        <div className="space-y-3 rounded-xl bg-[#2f2018] p-4">
          <div className="flex justify-between text-sm">
            <span className="text-[#cfae94]">Order ID</span>
            <span className="font-bold text-[#f6eadf]">{order.id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#cfae94]">Customer</span>
            <span className="font-bold text-[#f6eadf]">{order.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#cfae94]">Location</span>
            <span className="font-bold text-[#f6eadf]">{order.table}</span>
          </div>
          <div className="flex justify-between border-t border-white/8 pt-3 text-sm">
            <span className="text-[#cfae94]">Total</span>
            <span className="font-bold text-[#efbf93]">{order.total} NIS</span>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full rounded-lg bg-[#b8784e] py-2.5 text-sm font-bold uppercase text-white transition-colors hover:bg-[#a96739]"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
}

function AdminPage({ orders, updateOrderStatus, cancelOrder, tasks, analytics }) {
  const [completedTasks, setCompletedTasks] = useState([]);

  return (
    <div className="px-4 sm:px-8 py-12 space-y-12">
      {/* KPI Cards */}
      <div>
        <h2 className="mb-8 text-3xl font-bold text-[#f6eadf]">Today's Overview</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Orders", value: analytics.totalOrdersToday, icon: ShoppingCart, color: "text-blue-400" },
            { label: "Pending", value: analytics.pendingOrders, icon: AlertCircle, color: "text-yellow-400" },
            { label: "Preparing", value: analytics.preparingOrders, icon: ChefHat, color: "text-orange-400" },
            { label: "Ready", value: analytics.readyOrders, icon: CheckCircle2, color: "text-green-400" },
            { label: "Revenue", value: `${analytics.totalRevenueToday} NIS`, icon: TrendingUp, color: "text-[#e2b78d]" },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-[20px] border border-[#5b4032] bg-[#241812] p-4"
              >
                <Icon size={20} className={`mb-2 ${kpi.color}`} />
                <p className="text-xs text-[#cfae94]">{kpi.label}</p>
                <p className="mt-2 text-2xl font-bold text-[#f6eadf]">{kpi.value}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Barista Tasks */}
      <div>
        <h2 className="mb-8 text-3xl font-bold text-[#f6eadf]">Barista Tasks</h2>
        <div className="space-y-3">
          {tasks.map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-4 rounded-[20px] border border-[#5b4032] bg-[#241812] p-4"
            >
              <button
                onClick={() =>
                  setCompletedTasks(
                    completedTasks.includes(task.id)
                      ? completedTasks.filter((id) => id !== task.id)
                      : [...completedTasks, task.id]
                  )
                }
                className={`flex-shrink-0 ${completedTasks.includes(task.id) ? "text-green-500" : "text-[#d7a37a]"}`}
              >
                <CheckSquare size={24} />
              </button>
              <div className="flex-1">
                <p className={`font-bold ${completedTasks.includes(task.id) ? "line-through text-[#cfb49f]" : "text-[#f6eadf]"}`}>
                  {task.task}
                </p>
                <p className="mt-1 text-xs text-[#cfae94]">{task.time}</p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                  task.priority === "High"
                    ? "bg-red-600/30 text-red-300"
                    : task.priority === "Medium"
                    ? "bg-yellow-600/30 text-yellow-300"
                    : "bg-green-600/30 text-green-300"
                }`}
              >
                {task.priority}
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Orders */}
      <div>
        <h2 className="mb-8 text-3xl font-bold text-[#f6eadf]">Active Orders</h2>
        <div className="space-y-4">
          {orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-[20px] border border-[#5b4032] bg-[#241812] p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-lg font-bold text-[#f6eadf]">{order.name}</p>
                  <p className="text-xs text-[#cfae94]">{order.id} • {order.time}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    order.status === "Ready"
                      ? "bg-green-600/30 text-green-300"
                      : order.status === "Preparing"
                      ? "bg-blue-600/30 text-blue-300"
                      : "bg-yellow-600/30 text-yellow-300"
                  }`}
                >
                  {order.status}
                </span>
              </div>

              <div className="mb-4 rounded-xl bg-[#32231c] p-3 text-sm">
                <p className="mb-2 font-semibold text-[#f1d2b8]">Items:</p>
                <ul className="space-y-1 text-xs text-[#f4e5d8]">
                  {order.items.map((item, i) => (
                    <li key={i}>
                      {item.qty}× {item.name} – {item.qty * item.price} NIS
                    </li>
                  ))}
                </ul>
                {order.notes && <p className="mt-2 text-xs text-[#cfae94]">Note: {order.notes}</p>}
              </div>

              <div className="flex items-center justify-between">
                <p className="font-bold text-[#efbf93]">{order.total} NIS</p>
                <div className="flex gap-2">
                  {["Pending", "Preparing", "Ready"].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateOrderStatus(order.id, status)}
                      disabled={order.status === status}
                      className={`px-3 py-1.5 rounded text-xs font-bold uppercase transition-colors ${
                        order.status === status
                            ? "bg-[#8f5e3d] text-white"
                            : "bg-[#32231c] text-[#f6eadf] hover:bg-[#3d2a21]"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                  <button
                    onClick={() => cancelOrder(order.id)}
                    className="rounded px-3 py-1.5 text-xs font-bold uppercase transition-colors bg-red-600/30 text-red-300 hover:bg-red-600/50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AnalyticsPage({ analytics }) {
  return (
    <div className="px-4 sm:px-8 py-12 space-y-12">
      <h1 className="text-4xl font-bold text-[#f6eadf]">Business Insights</h1>

      {/* Key Metrics */}
      <div>
        <h2 className="mb-6 text-2xl font-bold text-[#f6eadf]">Performance Metrics</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { label: "Best Selling Item", value: analytics.bestSellingItem },
            { label: "Peak Hour", value: analytics.peakHour },
            { label: "Avg Order Value", value: `${analytics.avgOrderValue} NIS` },
          ].map((m, i) => (
            <div key={i} className="rounded-[20px] border border-[#5b4032] bg-[#241812] p-6">
              <p className="text-sm text-[#cfae94]">{m.label}</p>
              <p className="mt-3 text-2xl font-bold text-[#efbf93]">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Top Drinks */}
      <div>
        <h2 className="mb-6 text-2xl font-bold text-[#f6eadf]">Top Performing Drinks</h2>
        <div className="space-y-3">
          {analytics.topDrinks.map((drink, i) => (
            <div key={i} className="flex items-center justify-between rounded-[20px] border border-[#5b4032] bg-[#241812] p-4">
              <div className="flex-1">
                <p className="font-bold text-[#f6eadf]">{drink.name}</p>
                <p className="text-xs text-[#cfae94]">{drink.count} sold • {drink.revenue} NIS revenue</p>
              </div>
              <div className="h-2 w-32 overflow-hidden rounded-full bg-white/8">
                <div
                  className="h-full rounded-full"
                  style={{ background: "linear-gradient(90deg, #efbf93, #b8784e)", width: `${(drink.count / analytics.topDrinks[0].count) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insights */}
      <div>
        <h2 className="mb-6 text-2xl font-bold text-[#f6eadf]">AI-Generated Insights</h2>
        <div className="space-y-3">
          {analytics.insights.map((insight, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex gap-3 rounded-[20px] border border-[#5b4032] bg-gradient-to-r from-[#241812] to-[#32231c] p-4"
            >
              <Zap size={20} className="mt-0.5 flex-shrink-0 text-[#efbf93]" />
              <p className="text-sm text-[#f4e5d8]">{insight}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
